import { Button, Spinner } from '@heroui/react'
import { Pause, PlayIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export default function PlayHistoryButton({ generation, isActive, onPlayStateChange }) {
  const [isLoading, setIsLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const audioRef = useRef(null)
  const generationIdRef = useRef(null)

  // Handle cleanup when switching to a different audio
  useEffect(() => {
    // Only cleanup if we were playing but are no longer active
    if (!isActive && audioRef.current && generationIdRef.current !== null) {
      audioRef.current.pause()
      audioRef.current = null
      generationIdRef.current = null
      setIsPlaying(false)
      setIsPaused(false)
      setIsLoading(false)
    }
  }, [isActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const handlePlay = async () => {
    if (!generation.audioDataURL)
      return

    // If currently playing and not paused, pause it
    if (isPlaying && !isPaused && audioRef.current) {
      audioRef.current.pause()
      setIsPaused(true)
      return
    }

    // If paused, resume
    if (isPaused && audioRef.current) {
      try {
        await audioRef.current.play()
        setIsPaused(false)
      }
      catch (error) {
        console.error('Failed to resume audio:', error)
      }
      return
    }

    // Start new playback
    setIsLoading(true)

    try {
      // Notify parent that this button is becoming active
      onPlayStateChange(generation.id)
      generationIdRef.current = generation.id

      // Create new audio instance
      const audio = new Audio(generation.audioDataURL)
      audioRef.current = audio

      // Set up event handlers before playing
      audio.onloadstart = () => {
        setIsLoading(true)
      }

      audio.oncanplay = () => {
        setIsLoading(false)
      }

      audio.onplay = () => {
        setIsPlaying(true)
        setIsPaused(false)
        setIsLoading(false)
      }

      audio.onpause = () => {
        // Only set paused if we're still the active audio
        if (generationIdRef.current === generation.id) {
          setIsPaused(true)
        }
      }

      audio.onended = () => {
        // Reset all states when audio ends naturally
        setIsPlaying(false)
        setIsPaused(false)
        setIsLoading(false)
        audioRef.current = null
        generationIdRef.current = null
        onPlayStateChange(null)
      }

      audio.onerror = (error) => {
        console.error('Audio playback error:', error)
        // Reset all states on error
        setIsLoading(false)
        setIsPlaying(false)
        setIsPaused(false)
        audioRef.current = null
        generationIdRef.current = null
        onPlayStateChange(null)
      }

      // Start playback
      await audio.play()
    }
    catch (error) {
      console.error('Failed to play audio:', error)
      // Reset all states on error
      setIsLoading(false)
      setIsPlaying(false)
      setIsPaused(false)
      if (audioRef.current) {
        audioRef.current = null
      }
      generationIdRef.current = null
      onPlayStateChange(null)
    }
  }

  // Determine which icon to show
  const showPauseIcon = isPlaying && !isPaused && !isLoading

  return (
    <Button
      variant="light"
      isIconOnly
      disableRipple
      size="sm"
      color="primary"
      onPress={handlePlay}
      className="min-w-0"
      isDisabled={!generation.audioDataURL}
      spinner={<Spinner size="sm" variant="spinner" classNames={{ spinnerBars: 'bg-primary' }} />}
      isLoading={isLoading}
    >
      {!isLoading && (showPauseIcon ? <Pause size={16} /> : <PlayIcon size={16} />)}
    </Button>
  )
}
